import * as React from "react";
import { useMemo, useState } from "react";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { getOrderByStatus } from "../../../../api/order.api";
import { ikThumb } from "../../../../utils/image";
import MiniDashboard from "./MiniDashboard";

type Range = "today" | "7" | "30" | "all";
type Channel = "drinks" | "desserts";
type FilterStatus = "all" | "done" | "cancel" | "rejected";

const C = {
  espresso: "#5B3A29",
  caramel:  "#D97706",
  emerald:  "#059669",
  rose:     "#E11D48",
  amber:    "#F59E0B",
};
const HERO_GRAD = "linear-gradient(135deg,#FFFBEB 0%,#FEE2C7 45%,#FECACA 100%)";

interface OrderRow {
  id: number;
  status: string;
  orderDate: string;
  updatedAt: string;
  type: string;
  sweetness: number;
  tumblerImage?: string;
  menu?: {
    name: string;
    image?: string | null;
    category?: { categoryFor?: string; name?: string };
  } | null;
  user?: { name?: string } | null;
  cancelledBy?: { name?: string } | null;
}

const isDessertRow = (o: OrderRow): boolean => o.menu?.category?.categoryFor === "dessert";

const STATUS_BADGE: Record<
  string,
  { label: string; chipBg: string; chipFg: string; dot: string }
> = {
  done:        { label: "Done",       chipBg: "bg-emerald-50",  chipFg: "text-emerald-700",  dot: "bg-emerald-500" },
  picked:      { label: "Picked up",  chipBg: "bg-emerald-50",  chipFg: "text-emerald-700",  dot: "bg-emerald-500" },
  cancel:      { label: "Cancelled",  chipBg: "bg-red-50",      chipFg: "text-red-700",      dot: "bg-red-500" },
  rejected:    { label: "Rejected",   chipBg: "bg-red-50",      chipFg: "text-red-700",      dot: "bg-red-500" },
  ordered:     { label: "Ordered",    chipBg: "bg-ink-100",     chipFg: "text-ink-700",      dot: "bg-ink-400" },
  "in-progress": { label: "Brewing",  chipBg: "bg-amber-50",    chipFg: "text-amber-700",    dot: "bg-amber-500" },
};

const RANGE_OPTIONS: { id: Range; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "7",     label: "7 days" },
  { id: "30",    label: "30 days" },
  { id: "all",   label: "All time" },
];

const STATUS_FILTERS: { id: FilterStatus; label: string }[] = [
  { id: "all",      label: "All" },
  { id: "done",     label: "Done" },
  { id: "cancel",   label: "Cancelled" },
  { id: "rejected", label: "Rejected" },
];

// API treats endDate as `<=`, parsed as midnight UTC. Send the *next* day's
// date so the filter spans the whole final day. Same trick MiniDashboard uses.
function rangeToDates(r: Range): { startDate: string; endDate: string } {
  const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");
  switch (r) {
    case "today":
      return { startDate: moment().format("YYYY-MM-DD"), endDate: tomorrow };
    case "7":
      return { startDate: moment().subtract(7, "days").format("YYYY-MM-DD"), endDate: tomorrow };
    case "30":
      return { startDate: moment().subtract(30, "days").format("YYYY-MM-DD"), endDate: tomorrow };
    case "all":
      return { startDate: "2024-01-01", endDate: tomorrow };
  }
}

export default function BaristaHistoryTable() {
  const [range, setRange] = useState<Range>("today");
  const [channel, setChannel] = useState<Channel>("drinks");
  const [filter, setFilter] = useState<FilterStatus>("all");

  const { startDate, endDate } = rangeToDates(range);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["history", range, startDate, endDate],
    queryFn: async () => {
      // Fetch each terminal status per channel separately. Without the channel
      // split, high-volume drink orders eat the 100-row limit and desserts
      // never appear.
      const statuses = ["picked", "done", "cancel", "rejected"] as const;
      const channels = ["cafe", "dessert"] as const;
      const results = await Promise.all(
        statuses.flatMap((s) =>
          channels.map((c) => getOrderByStatus(s, startDate, endDate, c)),
        ),
      );
      return results.flat() as OrderRow[];
    },
  });

  // Counts per channel — used by the Drinks/Desserts toggle badges (ignore status filter).
  const channelCounts = useMemo(() => {
    const all = data ?? [];
    return {
      drinks:   all.filter((o) => !isDessertRow(o)).length,
      desserts: all.filter(isDessertRow).length,
    };
  }, [data]);

  // Counts per status, scoped to the active channel — drives the status chip badges.
  const counts = useMemo(() => {
    const channelRows = (data ?? []).filter((o) =>
      channel === "desserts" ? isDessertRow(o) : !isDessertRow(o),
    );
    return {
      all:      channelRows.length,
      done:     channelRows.filter((o) => o.status === "done" || o.status === "picked").length,
      cancel:   channelRows.filter((o) => o.status === "cancel").length,
      rejected: channelRows.filter((o) => o.status === "rejected").length,
    };
  }, [data, channel]);

  const visible = useMemo<OrderRow[]>(() => {
    if (!data) return [];
    let rows = data.filter((o) =>
      channel === "desserts" ? isDessertRow(o) : !isDessertRow(o),
    );
    if (filter === "done")     rows = rows.filter((o) => o.status === "done" || o.status === "picked");
    if (filter === "cancel")   rows = rows.filter((o) => o.status === "cancel");
    if (filter === "rejected") rows = rows.filter((o) => o.status === "rejected");
    return [...rows].sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [data, channel, filter]);

  const grouped = useMemo<Record<string, OrderRow[]>>(() => {
    const out: Record<string, OrderRow[]> = {};
    visible.forEach((o) => {
      const key = moment(o.updatedAt).format("LL");
      (out[key] ||= []).push(o);
    });
    return out;
  }, [visible]);

  const totalAll = channelCounts.drinks + channelCounts.desserts;

  return (
    <div className="min-h-screen font-sans pb-[80px]" style={{ background: "#FAFAF7" }}>
      <header className="px-5 pt-[24px] pb-[16px]">
        <div
          className="rounded-3xl p-5 md:p-6 relative overflow-hidden"
          style={{ background: HERO_GRAD, boxShadow: "0 12px 36px -16px rgba(217,119,6,0.25)" }}
        >
          <div
            aria-hidden
            className="absolute -right-12 -top-12 w-[180px] h-[180px] rounded-full opacity-25"
            style={{ background: "radial-gradient(circle,#92400E 0%,transparent 70%)" }}
          />
          <p className="text-[11px] font-bold tracking-[0.12em] uppercase relative" style={{ color: C.espresso }}>
            📜 History
          </p>
          <h1
            className="font-display font-black mt-2 leading-[0.95] tracking-tightest relative"
            style={{ color: "#1B0F08", fontSize: "clamp(34px,8vw,52px)" }}
          >
            {totalAll.toLocaleString()}
          </h1>
          <p className="font-semibold mt-1 relative" style={{ color: C.espresso, fontSize: "14px" }}>
            orders in this window
          </p>
        </div>
      </header>

      <MiniDashboard />

      {/* Channel toggle */}
      <div className="px-5 pt-3">
        <div className="flex gap-2 p-1 rounded-2xl" style={{ background: "white", boxShadow: "0 0 0 1px #E7DFD2" }}>
          {([
            { id: "drinks",   label: "Drinks",   emoji: "☕", count: channelCounts.drinks },
            { id: "desserts", label: "Desserts", emoji: "🍰", count: channelCounts.desserts },
          ] as const).map((c) => {
            const active = channel === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => { setChannel(c.id); setFilter("all"); }}
                className="flex-1 inline-flex items-center justify-center gap-2 h-[40px] rounded-xl text-sm font-bold transition-all"
                style={
                  active
                    ? { background: C.espresso, color: "#FFFBEB" }
                    : { background: "transparent", color: C.espresso }
                }
              >
                <span aria-hidden>{c.emoji}</span>
                <span>{c.label}</span>
                <span
                  className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[10px] font-black tabular-nums"
                  style={
                    active
                      ? { background: "rgba(255,251,235,0.25)", color: "#FFFBEB" }
                      : { background: "#F3EBDA", color: C.espresso }
                  }
                >
                  {c.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Date range chips */}
      <div className="px-5 pt-3">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {RANGE_OPTIONS.map((opt) => {
            const active = range === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setRange(opt.id)}
                className="shrink-0 inline-flex items-center h-[34px] px-4 rounded-full text-xs font-bold uppercase tracking-wider transition-all"
                style={
                  active
                    ? { background: C.espresso, color: "#FFFBEB" }
                    : { background: "white", color: C.espresso, boxShadow: "0 0 0 1px #E7DFD2" }
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Status filter chips */}
      <div className="px-5 pt-2.5">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {STATUS_FILTERS.map((s) => {
            const count =
              s.id === "all" ? counts.all :
              s.id === "done" ? counts.done :
              s.id === "cancel" ? counts.cancel :
              counts.rejected;
            const active = filter === s.id;
            const tint =
              s.id === "done"     ? C.emerald
              : s.id === "cancel"   ? C.amber
              : s.id === "rejected" ? C.rose
              : C.espresso;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setFilter(s.id)}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 h-[30px] rounded-full text-xs font-bold transition-all"
                style={
                  active
                    ? { background: tint, color: "#FFFFFF" }
                    : { background: "white", color: tint, boxShadow: `0 0 0 1px ${tint}33` }
                }
              >
                {s.label}
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-[16px] px-1 rounded-full text-[10px] font-black tabular-nums"
                  style={
                    active
                      ? { background: "rgba(255,255,255,0.25)", color: "#FFFFFF" }
                      : { background: `${tint}22`, color: tint }
                  }
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-[80px]">
          <svg className="w-7 h-7 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: C.caramel }}>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.2" />
            <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="mx-5 mt-[40px] bg-white rounded-2xl py-8 text-center" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
          <p className="font-display font-black text-lg" style={{ color: "#1B0F08" }}>Could not load history</p>
          <p className="text-sm mt-1" style={{ color: "#A38B6A" }}>{(error as any)?.message ?? "Try again"}</p>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && visible.length === 0 && (
        <div className="mx-5 mt-[40px] bg-white rounded-2xl py-[60px] flex flex-col items-center text-center" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
          <div className="w-[56px] h-[56px] rounded-full flex items-center justify-center mb-4 text-3xl" style={{ background: "#F3EBDA" }}>
            📭
          </div>
          <p className="font-display font-black text-lg" style={{ color: "#1B0F08" }}>No history found</p>
          <p className="text-sm mt-1" style={{ color: "#A38B6A" }}>Try a different range or filter</p>
        </div>
      )}

      {/* Grouped list */}
      {!isLoading && !isError && visible.length > 0 && (
        <main className="px-5 pt-5">
          {Object.entries(grouped).map(([date, rows]) => {
            const completed = rows.filter((o) => o.status === "done" || o.status === "picked").length;
            const cancelled = rows.filter((o) => o.status === "cancel" || o.status === "rejected").length;
            return (
              <section key={date} className="mb-6">
                <div className="flex items-center justify-between pb-2">
                  <h3 className="font-display font-black text-[14px] tracking-wide" style={{ color: C.espresso }}>
                    {date}
                  </h3>
                  <p className="text-xs font-bold">
                    <span style={{ color: C.emerald }}>{completed} done</span>
                    {cancelled > 0 && (
                      <>
                        <span className="mx-1.5" style={{ color: "#A38B6A" }}>·</span>
                        <span style={{ color: C.rose }}>{cancelled} cancelled</span>
                      </>
                    )}
                  </p>
                </div>

                <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: "0 1px 0 #ECE5D5,0 4px 24px -16px rgba(91,58,41,0.18)" }}>
                  {rows.map((o, idx) => {
                    const badge = STATUS_BADGE[o.status] ?? STATUS_BADGE.ordered;
                    const isCancelled = o.status === "cancel" || o.status === "rejected";
                    const cancelledByName = o.cancelledBy?.name;
                    const isDessert = isDessertRow(o);
                    const imgSrc = isDessert ? (o.menu?.image ?? "") : (o.tumblerImage ?? "");
                    const initial = (o.menu?.name ?? "?").trim().slice(0, 1).toUpperCase();
                    return (
                      <div
                        key={o.id}
                        className="flex items-center gap-3 px-4 py-3"
                        style={{ borderTop: idx === 0 ? "0" : "1px solid #F3EBDA" }}
                      >
                        <div
                          className="flex-shrink-0 w-[56px] h-[56px] rounded-2xl overflow-hidden flex items-center justify-center relative"
                          style={{ background: "#F3EBDA" }}
                        >
                          {imgSrc ? (
                            <img
                              src={ikThumb(imgSrc, 160, 160)}
                              alt={o.menu?.name ?? ""}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <span className="font-display font-black text-2xl" style={{ color: "#A38B6A" }}>{initial}</span>
                          )}
                          <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${badge.dot}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold" style={{ color: "#A38B6A" }}>
                              {moment(o.updatedAt).format("HH:mm")}
                            </span>
                            <span className={`inline-flex items-center px-2 h-[20px] rounded-full text-[10px] font-bold uppercase tracking-wider ${badge.chipBg} ${badge.chipFg}`}>
                              {badge.label}
                            </span>
                            {isDessert && (
                              <span className="inline-flex items-center px-2 h-[20px] rounded-full text-[10px] font-bold bg-pink-100 text-pink-900">
                                🍰 Dessert
                              </span>
                            )}
                          </div>
                          <div className="font-display font-black text-base truncate mt-0.5" style={{ color: "#1B0F08" }}>
                            {o.menu?.name ?? "(deleted menu)"}
                          </div>
                          <div className="text-xs truncate" style={{ color: "#A38B6A" }}>
                            {isDessert
                              ? (o.menu?.category?.name ?? "Dessert")
                              : `${o.type ?? "—"} · ${o.sweetness ?? 100}% sweet`}
                            {o.user?.name && (
                              <>
                                <span className="mx-1.5">·</span>
                                <span className="font-semibold">{o.user.name}</span>
                              </>
                            )}
                          </div>
                          {isCancelled && cancelledByName && (
                            <div className="text-xs mt-0.5 font-semibold" style={{ color: C.rose }}>
                              Cancelled by <span className="font-black">{cancelledByName}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </main>
      )}
    </div>
  );
}
